'use client';

import { Divider, Input, Select, UnstyledButton } from '@mantine/core';
import { Button } from '@scaleup/frontend/ui';
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react';
import {
  NAICS,
  naicsHierarchy,
  naicsDictionary,
  Supplier,
  SupplierData,
} from '@scaleup/models';
import { IntakeBase } from './common';
import { useSupplierIntakeContext } from './context';
import { NDrop } from './naics-drop';
import { useState } from 'react';
import { on } from 'events';
import { dir } from 'console';

export const Naics = () => {
  const {
    setCurrentStep,
    supplier,
    naics,
    setNAICS,
    primaryNAICS,
    setPrimaryNAICS,
    updateSupplier,
    lastStep,
    setLastStep,
  } = useSupplierIntakeContext();
  const [naicsEntry, setNaicsEntry] = useState<string>('');
  const [naicsEntryLabel, setNaicsEntryLabel] = useState<string>('');
  const [showNaicsEntry, setShowNaicsEntry] = useState<boolean>(false);
  const [industries] = useState<string[]>(Object.keys(naicsHierarchy));
  const [industry, setIndustry] = useState<string>('');
  const [subcategories, setSubcategories] = useState<string[]>([]);
  const [subcategory, setSubcategory] = useState<string>('');
  const [naicsSelect, setNaicsSelect] = useState<NAICS[]>([]);
  const [loadingNaics, setLoadingNaics] = useState<boolean>(false);
  const [dirty, setDirty] = useState<boolean>(false);

  const handleIndustryChange = (value: string) => {
    if (value) {
      setIndustry(value);
      setSubcategory('');
      const hierarchy = naicsHierarchy[value as keyof typeof naicsHierarchy];
      setSubcategories(Object.keys(hierarchy));
    }
  };

  const handleSubcategoryChange = (value: string) => {
    if (value) {
      setSubcategory(value);
      const hierarchy = naicsHierarchy[industry as keyof typeof naicsHierarchy];
      const codes: NAICS[] = hierarchy[value as keyof typeof hierarchy];
      const existingCodes = naics.map((n: NAICS) => n.code);
      const filteredCodes = codes.filter(
        (code: NAICS) => !existingCodes.includes(code.code)
      );
      setNaicsSelect(filteredCodes);
    } else {
      setNaicsSelect([]);
    }
  };
  const handleNAICSSearch = (value: string) => {
    if (naics.length >= 20) return;
    setNaicsEntry(value);
    if (value.length > 5) {
      const found = naicsDictionary[value as keyof typeof naicsDictionary];
      if (found) {
        setShowNaicsEntry(true);
        setNaicsEntryLabel(found.value.label);
      } else {
        setShowNaicsEntry(false);
      }
    } else {
      setShowNaicsEntry(false);
    }
  };

  const addNAICSFromSearch = (code: string) => {
    if (naics.length >= 20) return;
    const found = naicsDictionary[code as keyof typeof naicsDictionary];
    if (found) {
      const existingCodes = naics.map((n: NAICS) => n.code);
      if (!existingCodes.includes(code)) {
        let temp = [...naics];
        temp.push({
          code: code,
          label: found.value.label,
          description: found.value.label,
        });
        setNAICS(temp);
        setNaicsEntry('');
        setNaicsEntryLabel('');
        setShowNaicsEntry(false);
        setDirty(true);
      }
    }
  };

  const addNAICSFromSelect = (code: string) => {
    if (naics.length >= 20) return;
    const found = naicsDictionary[code as keyof typeof naicsDictionary];
    if (found) {
      const existingCodes = naics.map((n: NAICS) => n.code);
      if (!existingCodes.includes(code)) {
        let temp = [...naics];
        temp.push({
          code: code,
          label: found.value.label,
          description: found.value.label,
        });
        setNAICS(temp);
        setDirty(true);
        const newNAICSSelect = naicsSelect.filter((item) => item.code !== code);
        setNaicsSelect(newNAICSSelect);
        if (naics.length >= 20) {
          setSubcategory('');
          setIndustry('');
          setSubcategories([]);
          setNaicsSelect([]);
        }
      }
    }
  };

  const handleClick = async () => {
    // add api call
    if (!dirty) {
      setCurrentStep('review');
    }

    const supplierData: SupplierData = {
      ...supplier.supplierData,
      naics: naics,
      primaryNAICS: primaryNAICS,
    };
    setLoadingNaics(true);
    updateSupplier({
      ...supplier,
      supplierData: supplierData,
    }).then((newSupplier: Supplier) => {
      setLoadingNaics(false);
      setCurrentStep('review');
      console.log('Updated Supplier:', newSupplier);
    });
  };

  return (
    <IntakeBase currentStep={4}>
      <div className="flex flex-col gap-2">
        <p className="text-text-primary font-bold text-xl">NAICS Codes</p>

        <p className="text-text-secondary">
          We've pre-selected NAICS codes based on your industry. Please review
          these codes in the left column. Use the column on the right to search
          for and add additional NAICS codes. You can add up to 20.
        </p>
      </div>

      <div className="rounded-[1rem] bg-primary-25 p-8 my-8 w-full gap-4 items-center justify-center">
        <div className="flex gap-4">
          <div className="w-2/5 flex flex-col gap-4">
            <p className="text-text-primary font-bold text-xl text-center pb-5">
              Your NAICS Codes List
            </p>
            <NDrop setDirty={setDirty} />
          </div>
          <div>
            <Divider
              color="#c6e7ef"
              orientation="vertical"
              className="h-full"
            />
          </div>
          <div className="w-3/5">
            <p className="text-text-primary font-bold text-xl text-center pb-5">
              Already know your code?
            </p>
            <p className="text-[#1D2939] font-normal text-base leading-6 text-center">
              Search NAICS codes by keyword or number
            </p>
            <Input
              radius="xl"
              value={naicsEntry}
              onChange={(value) => {
                handleNAICSSearch(value.currentTarget.value);
              }}
              placeholder="Search NAICS codes..."
              className="w-2/3 m-auto mt-6"
            />
            <div className="h-[60px] mt-3 w-fit m-auto">
              {showNaicsEntry && (
                <div className="rounded-lg flex flex-row pt-2 pb-2 bg-white shadow-[0_0_8px_0_rgba(182,192,221,0.10),0_0_4px_0_rgba(182,192,221,0.20)]">
                  <span className="m-2 text-[#1D2939] font-normal text-base leading-6">
                    {naicsEntryLabel}
                  </span>
                  <Divider
                    color="#c6e7ef"
                    orientation="vertical"
                    className="bg-white"
                  />
                  <UnstyledButton
                    className="m-2 text-[#377D91] font-semibold text-base leading-6"
                    onClick={() => addNAICSFromSearch(naicsEntry)}
                  >
                    Add
                  </UnstyledButton>
                </div>
              )}
            </div>
            <Divider
              color="#c6e7ef"
              orientation="horizontal"
              className="w-full my-4"
            />
            <p className="text-text-primary font-bold text-xl text-center pb-5">
              Don’t know your code?{' '}
            </p>
            <p className="text-[#1D2939] font-normal text-base leading-6 text-center">
              Choose an industry and subcategory to view relevant NAICS codes.{' '}
            </p>
            <div className="flex gap-4 w-full mt-6">
              <div className="w-1/2">
                <Select
                  required
                  label="Industry"
                  classNames={{ input: 'w-full' }}
                  data={industries}
                  onChange={(value) => handleIndustryChange(value || '')}
                  disabled={naics.length >= 20}
                  value={industry}
                />
              </div>
              <div className="w-1/2">
                <Select
                  required
                  label="Subcategory"
                  classNames={{ input: 'w-full' }}
                  data={subcategories}
                  onChange={(value) => handleSubcategoryChange(value || '')}
                  disabled={!industry || naics.length >= 20}
                  value={subcategory}
                />
              </div>
            </div>
            <div className="mt-4 w-fit m-auto">
              {naicsSelect.length > 0 &&
                naicsSelect.map((naicsItem) => (
                  <div
                    key={naicsItem.code}
                    className="rounded-lg w-fit flex flex-row mt-4 pt-2 pb-2 bg-white shadow-[0_0_8px_0_rgba(182,192,221,0.10),0_0_4px_0_rgba(182,192,221,0.20)]"
                  >
                    <span className="m-2 text-[#1D2939] font-normal text-base leading-6">
                      {naicsItem.label}
                    </span>
                    <Divider
                      color="#c6e7ef"
                      orientation="vertical"
                      className="bg-white"
                    />
                    <UnstyledButton
                      className="m-2 text-[#377D91] font-semibold text-base leading-6"
                      onClick={() => addNAICSFromSelect(naicsItem.code)}
                    >
                      Add
                    </UnstyledButton>
                  </div>
                ))}
            </div>
          </div>
        </div>
        <p className="text-[#377D91] font-semibold text-base leading-6 relative float-right">
          {naics.length} of 20 Selected
        </p>
      </div>

      <div className="flex justify-between">
        <Button
          text="Back"
          variant="secondary"
          prefixIcon={<IconArrowLeft />}
          onClick={() => {
            if (lastStep === 'review') {
              setLastStep('naics');
              setCurrentStep('review');
            } else {
              setLastStep('naics');
              setCurrentStep('structure');
            }
          }}
        />

        <Button
          text="Continue"
          variant="primary"
          suffixIcon={<IconArrowRight />}
          onClick={handleClick}
          disabled={naics.length === 0 || naics.length > 20}
          loading={loadingNaics}
        />
      </div>
    </IntakeBase>
  );
};
